import { PublicClientApplication, type AccountInfo, type AuthenticationResult } from "@azure/msal-browser";

const LOCAL_DEV_HOSTNAMES = new Set(["localhost", "127.0.0.1"]);
const ADDIN_HOSTNAMES = new Set(["addin.velyoo.com", "dev.addin.velyoo.com", "test.addin.velyoo.com"]);
const DIALOG_AUTH_SESSION_KEY = "velyoo-addin-dialog-auth-session";

type DialogAuthSession = {
  accessToken: string;
  account: AccountInfo;
  expiresOn: string | null;
  scopes: string[];
};

type DialogAuthMessage =
  | { type: "velyoo-auth-success"; result: SerializableAuthResult }
  | { type: "velyoo-auth-error"; message: string };

type SerializableAuthResult = {
  accessToken: string;
  account: AccountInfo;
  expiresOn: string | null;
  scopes: string[];
};

export type LoginResult = {
  account: AccountInfo | null;
  method: "cached" | "office-dialog" | "msal-redirect";
};

function getRequiredConfig(name: string) {
  const value = String(import.meta.env[name] ?? "").trim();
  if (!value || value.startsWith("<") || value.endsWith(">")) {
    throw new Error(`${name} is not configured for this add-in build.`);
  }
  return value;
}

function resolveRedirectUri() {
  const configuredRedirectUri = String(import.meta.env.ADDIN_VITE_MSAL_REDIRECT_URI ?? "").trim();
  if (typeof window === "undefined") {
    return configuredRedirectUri;
  }

  const currentOrigin = window.location.origin;
  if (!configuredRedirectUri) {
    return currentOrigin;
  }

  const configuredUrl = new URL(configuredRedirectUri, currentOrigin);
  const isLocalhostOrigin = LOCAL_DEV_HOSTNAMES.has(window.location.hostname);
  const isMappedAddinOrigin = ADDIN_HOSTNAMES.has(window.location.hostname);

  if ((isLocalhostOrigin || isMappedAddinOrigin) && configuredUrl.origin !== currentOrigin) {
    return currentOrigin;
  }

  return configuredUrl.toString();
}

function resolveDialogRedirectUri() {
  const configuredRedirectUri = String(import.meta.env.ADDIN_VITE_MSAL_DIALOG_REDIRECT_URI ?? "").trim();
  if (configuredRedirectUri) {
    return configuredRedirectUri;
  }

  if (typeof window === "undefined") {
    return "";
  }

  return `${window.location.origin}/auth-dialog.html`;
}

function resolveKnownAuthorities(authority: string) {
  try {
    return [new URL(authority).host];
  } catch {
    return [];
  }
}

function isMissingRedirectRequestCacheError(error: unknown) {
  const candidate = error as { code?: string; errorCode?: string; message?: string } | null;

  return candidate?.errorCode === "no_token_request_cache_error"
    || candidate?.code === "no_token_request_cache_error"
    || String(candidate?.message ?? "").includes("no_token_request_cache_error");
}

function clearAuthResponseFromUrl() {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  let changed = false;

  if (url.hash && /(?:^|[#&])(?:code|state|error|error_description)=/.test(url.hash)) {
    url.hash = "";
    changed = true;
  }

  if (url.searchParams.has("code") || url.searchParams.has("state") || url.searchParams.has("error")) {
    url.searchParams.delete("code");
    url.searchParams.delete("state");
    url.searchParams.delete("session_state");
    url.searchParams.delete("client_info");
    url.searchParams.delete("error");
    url.searchParams.delete("error_description");
    changed = true;
  }

  if (changed) {
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  }
}

function readDialogAuthSession(): DialogAuthSession | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawSession = window.localStorage.getItem(DIALOG_AUTH_SESSION_KEY);
    return rawSession ? JSON.parse(rawSession) as DialogAuthSession : null;
  } catch {
    return null;
  }
}

function writeDialogAuthSession(result: SerializableAuthResult) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(DIALOG_AUTH_SESSION_KEY, JSON.stringify({
    accessToken: result.accessToken,
    account: result.account,
    expiresOn: result.expiresOn,
    scopes: result.scopes,
  }));
}

function clearStoredAuthSession() {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(DIALOG_AUTH_SESSION_KEY);
  window.localStorage.removeItem("velyoo-addin-office-sso-account");
}

function hasUsableDialogToken(session: DialogAuthSession | null) {
  if (!session?.accessToken) {
    return false;
  }

  if (!session.expiresOn) {
    return true;
  }

  return new Date(session.expiresOn).getTime() > Date.now() + 60_000;
}

function isOfficeDialogAvailable() {
  return typeof Office !== "undefined" && Boolean(Office.context?.ui?.displayDialogAsync);
}

let msalInstance: PublicClientApplication | null = null;
let initializationPromise: Promise<void> | null = null;

function createMsalInstance() {
  const authority = getRequiredConfig("ADDIN_VITE_MSAL_AUTHORITY");

  return new PublicClientApplication({
    auth: {
      authority,
      clientId: getRequiredConfig("ADDIN_VITE_MSAL_CLIENT_ID"),
      knownAuthorities: resolveKnownAuthorities(authority),
      redirectUri: resolveRedirectUri(),
    },
    cache: {
      cacheLocation: "localStorage",
    },
  });
}

export async function initializeMsal(): Promise<AccountInfo | null> {
  if (!initializationPromise) {
    initializationPromise = (async () => {
      if (!msalInstance) {
        msalInstance = createMsalInstance();
      }

      await msalInstance.initialize();

      let redirectResponse = null;
      try {
        redirectResponse = await msalInstance.handleRedirectPromise({
          navigateToLoginRequestUrl: false,
        });
      } catch (error) {
        if (!isMissingRedirectRequestCacheError(error)) {
          throw error;
        }

        console.warn("Ignoring MSAL redirect response without matching request cache", error);
        clearAuthResponseFromUrl();
      }

      if (redirectResponse?.account) {
        msalInstance.setActiveAccount(redirectResponse.account);
        return;
      }

      const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
      if (account) {
        msalInstance.setActiveAccount(account);
      }
    })();
  }

  await initializationPromise;
  return getActiveAccount();
}

export function getActiveAccount(): AccountInfo | null {
  return msalInstance?.getActiveAccount()
    ?? msalInstance?.getAllAccounts()[0]
    ?? readDialogAuthSession()?.account
    ?? null;
}

export async function logoutFromAddin() {
  clearStoredAuthSession();

  if (!msalInstance) {
    msalInstance = createMsalInstance();
    await msalInstance.initialize();
  } else if (initializationPromise) {
    await initializationPromise;
  }

  const accounts = msalInstance.getAllAccounts();
  for (const account of accounts) {
    await msalInstance.clearCache({ account });
  }

  await msalInstance.clearCache();
  msalInstance.setActiveAccount(null);
}

function loginWithOfficeDialog(loginHint?: string) {
  return new Promise<AccountInfo | null>((resolve, reject) => {
    const dialogUrl = new URL("/auth-dialog.html", window.location.origin);
    if (loginHint) {
      dialogUrl.searchParams.set("loginHint", loginHint);
    }

    Office.context.ui.displayDialogAsync(
      dialogUrl.toString(),
      { displayInIframe: false, height: 60, width: 40 },
      (asyncResult) => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(asyncResult.error?.message ?? "Could not open Microsoft sign-in dialog."));
          return;
        }

        const dialog = asyncResult.value;
        let settled = false;

        const settle = (callback: () => void) => {
          if (settled) {
            return;
          }

          settled = true;
          dialog.close();
          callback();
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (event) => {
          const rawMessage = "message" in event ? event.message : "";

          try {
            const message = JSON.parse(rawMessage) as DialogAuthMessage;
            if (message.type === "velyoo-auth-success") {
              writeDialogAuthSession(message.result);
              settle(() => resolve(message.result.account));
              return;
            }

            settle(() => reject(new Error(message.message)));
          } catch (error) {
            settle(() => reject(error instanceof Error ? error : new Error("Invalid sign-in response.")));
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (event) => {
          const error = "error" in event ? event.error : undefined;
          if (!settled && error !== 12006) {
            settle(() => reject(new Error(`Microsoft sign-in dialog closed unexpectedly (${error ?? "unknown"}).`)));
          }
        });
      },
    );
  });
}

export async function loginWithMicrosoft(loginHint?: string): Promise<LoginResult> {
  const account = await initializeMsal();
  if (account) {
    return { account, method: "cached" };
  }

  if (isOfficeDialogAvailable()) {
    return {
      account: await loginWithOfficeDialog(loginHint),
      method: "office-dialog",
    };
  }

  if (!msalInstance) {
    throw new Error("Login is not initialized.");
  }

  const scopes = [getRequiredConfig("ADDIN_VITE_MSAL_API_SCOPE")];
  await msalInstance.loginRedirect({
    loginHint,
    scopes,
  });
  return {
    account: null,
    method: "msal-redirect",
  };
}

export async function acquireApiToken() {
  await initializeMsal();

  const account = getActiveAccount();
  if (msalInstance && account) {
    try {
      const result = await msalInstance.acquireTokenSilent({
        account,
        scopes: [getRequiredConfig("ADDIN_VITE_MSAL_API_SCOPE")],
      });

      return result.accessToken;
    } catch (error) {
      console.warn("Silent MSAL token acquisition failed; checking dialog session token.", error);
    }
  }

  const dialogSession = readDialogAuthSession();
  if (hasUsableDialogToken(dialogSession)) {
    return dialogSession?.accessToken ?? null;
  }

  return null;
}

export function serializeAuthResult(result: AuthenticationResult): SerializableAuthResult {
  return {
    accessToken: result.accessToken,
    account: result.account,
    expiresOn: result.expiresOn?.toISOString() ?? null,
    scopes: result.scopes,
  };
}

export function createDialogMsalInstance() {
  const authority = getRequiredConfig("ADDIN_VITE_MSAL_AUTHORITY");

  return new PublicClientApplication({
    auth: {
      authority,
      clientId: getRequiredConfig("ADDIN_VITE_MSAL_CLIENT_ID"),
      knownAuthorities: resolveKnownAuthorities(authority),
      redirectUri: resolveDialogRedirectUri(),
    },
    cache: {
      cacheLocation: "localStorage",
    },
  });
}

export function getApiScopes() {
  return [getRequiredConfig("ADDIN_VITE_MSAL_API_SCOPE")];
}
